import {useCallback, useEffect, useRef} from 'react';
import toast from 'react-hot-toast';
import {MessageType} from "../../enum";


export const useMessageHandler = ({
                                      messages, userId, myChannel, rtcStatus,
                                      endWebRTCCall, setParticipants, setOtherParticipant,
                                      setMyChannel, setOtherParticipantMuted, handleTranscription, sendMessage, roomCode
                                  }) => {
    const processedMessagesRef = useRef(new Set());
    const participantsCacheRef = useRef([]);
    const lastUpdateTimeRef = useRef(0);

    const updateParticipants = useCallback((participantsList) => {
        const now = Date.now();

        if (now - lastUpdateTimeRef.current < 500 &&
            JSON.stringify(participantsCacheRef.current) === JSON.stringify(participantsList)) {
            return;
        }

        lastUpdateTimeRef.current = now;
        participantsCacheRef.current = participantsList;

        setParticipants(participantsList);

        const myIndex = participantsList.findIndex(p => p.id === userId);
        if (myIndex !== -1 && myChannel === null) {
            setMyChannel(myIndex);
        }

        const other = participantsList.find(p => p.id !== userId);
        setOtherParticipant(other || null);
    }, [userId, myChannel, setParticipants, setMyChannel, setOtherParticipant]);


    const handleUserJoined = useCallback((msg) => {
        if (msg.user_id === userId) return;

        const totalParticipants = msg.total_participants || 2;
        toast.success(
            `${msg.user_name} joined • ${totalParticipants} participant${totalParticipants > 1 ? 's' : ''}`,
            {
                position: 'bottom-center',
                duration: 3000,
                icon: '👋'
            }
        );

        setParticipants((prev) => {
            const exists = prev.some(p => p.id === msg.user_id);
            if (exists) return prev;
            return [...prev, {id: msg.user_id, name: msg.user_name}];
        });

        setOtherParticipant({id: msg.user_id, name: msg.user_name});
    }, [userId, setParticipants, setOtherParticipant]);

    const handleUserLeft = useCallback((msg) => {
        if (msg.user_id === userId) return;

        setParticipants((prev) => {
            const updated = prev.filter((p) => p.id !== msg.user_id);
            const remainingCount = updated.length;

            toast(
                `${msg.user_name} left • ${remainingCount} participant${remainingCount > 1 ? 's' : ''}`,
                {
                    position: 'bottom-center',
                    duration: 3000,
                    icon: '👋'
                }
            );

            return updated;
        });

        if (rtcStatus === 'connected' || rtcStatus === 'connecting') {
            endWebRTCCall();
            toast('Call ended - participant left', {
                position: 'bottom-center',
                duration: 2000,
                icon: '📞'
            });
        }

        setOtherParticipant(null);
    }, [userId, rtcStatus, endWebRTCCall, setParticipants, setOtherParticipant]);


    useEffect(() => {
        if (messages.length === 0) return;
        const newMessages = messages.filter(msg => {
            const msgId = `${msg.type}_${msg.user_id || ''}_${msg.timestamp || Date.now()}`;
            return !processedMessagesRef.current.has(msgId);
        });

        newMessages.forEach((msg) => {
            const msgId = `${msg.type}_${msg.user_id || ''}_${msg.timestamp || Date.now()}`;
            processedMessagesRef.current.add(msgId);

            const messageType = msg.type || msg.msg_type;

            console.log('[Room] Processing message:', messageType);

            switch (msg.type || msg.msg_type) {
                case 'participants_list':
                case 'participants_sync':
                    updateParticipants(msg.participants || []);
                    break;

                case 'user_joined':
                    handleUserJoined(msg);
                    break;

                case 'user_left':
                    handleUserLeft(msg);
                    break;

                case 'call_ended':
                    console.log('[Room] 📞 Call ended');
                    endWebRTCCall();
                    if (msg.user_id !== userId) {
                        toast(`Call ended by ${msg.user_name}`, {
                            position: 'bottom-center',
                            duration: 2000,
                            icon: '📞'
                        });
                    }
                    break;

                case 'mute_status':
                    if (msg.user_id !== userId) {
                        setOtherParticipantMuted(msg.is_muted);
                    }
                    break;

                case MessageType.TRANSCRIPT:
                case 'transcription':
                    handleTranscription(msg);
                    break;

                case 'connection_status':
                    console.log('[Room] Connection status:', msg);
                    break;

                case 'transcription_status':
                    console.log(msg)
                    if (msg.status === 'disconnected') {
                        toast.error('Transcription service disconnected. Audio may not be transcribed.');
                    } else if (msg.status === 'connected') {
                        toast.success('Transcription service connected');
                    }
                    break;

                case 'error':
                    toast.error(msg.error || 'An error occurred', {
                        duration: 3000,
                        position: 'bottom-center',
                    });
                    break;

                case 'call_started':
                    console.log('[Room] Call started:', msg);
                    break;

                default:
                    console.log('[Room] Unhandled message:', msg.type || msg.msg_type);
            }
        });

        if (processedMessagesRef.current.size > 100) {
            const entries = Array.from(processedMessagesRef.current);
            processedMessagesRef.current = new Set(entries.slice(-100));
        }
    }, [messages, updateParticipants, handleUserJoined, handleUserLeft, handleTranscription, endWebRTCCall, userId, setOtherParticipantMuted]);

    return null;
};
